import { nil, NO, UICore, UITextView, UIView, UIViewBroadcastEvent, YES } from "uicore-ts"
import { RHProcurement, RHProcurementPublic } from "../../../../models/prodModels"
import { RHCore } from "../../Core/RHCore"
import { RHColors, RHFont, RHSpacing } from "../../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHPrivateMessageThreadViewTokens } from "../../Custom components/RH/Utils_slop/RHViewTokens"
import { RHOffererCompanyEligibleForPrivateMessage, RHProcurementPrivateMessageThread, RHProcurementPrivateMessageThreadsSnapshot } from "../../SocketClientFunctions"
import { RHPrivateMessageThreadListView } from "./RHPrivateMessageThreadListView"
import { RHPrivateMessageThreadView } from "./RHPrivateMessageThreadView"
import { RHStartPrivateMessageThreadDialogView } from "./RHStartPrivateMessageThreadDialogView"
import { RHDropdown } from "../../Custom components/RH/RHDropdown"
import type { ProcurementRoundContext } from "../ProcurementRoundContext"


type RHPrivateMessagingAccessState = {
    isCurrentUserActingAsProcurer: boolean
    canAnswerMessages: boolean
    shouldReloadVisibleView?: boolean
}


/**
 * The panel shown for the "Privaatne" Teabevahetus subtab. A procurer who can
 * send messages sees a thread list (left) plus the selected thread (right): the list is
 * pre-populated with every offerer company eligible for private messaging,
 * not only ones with an existing message, so clicking straight into a single
 * company's row and typing is the direct path — "Saada sõnum" (bulk send) is
 * only needed to message several companies with the same opening message at
 * once. A procuring-team member who can only view (not send) sees the list
 * read-only, with no empty rows to click into. An eligible offerer company
 * member sees their own single thread and may send its first message.
 */
export class RHPrivateMessagingView extends UIView {

    readonly threadListView = new RHPrivateMessageThreadListView()

    readonly loadingLabel = new UITextView()
        .configuredWithObject({
            text: "Laadimine…",
            textColor: RHColors.textTimestamp,
            style: {
                fontSize: RHFont.sizeSmall,
                fontStyle: "italic",
                textAlign: "center"
            }
        })

    readonly noSelectionLabel = new UITextView()
        .configuredWithObject({
            text: "Vali vestlus või saada sõnum pakkujatele.",
            textColor: RHColors.textTimestamp,
            style: {
                fontSize: RHFont.sizeSmall,
                fontStyle: "italic",
                textAlign: "center"
            }
        })

    readonly threadView = new RHPrivateMessageThreadView()

    readonly startDialog = new RHStartPrivateMessageThreadDialogView()
    readonly composeTargetDropdown = new RHDropdown<string>().configuredWithObject({
        labelText: "Sõnumi kontekst",
        controlEventTargetAccumulator: {
            SelectionDidChange: () => this._renderSelectedThread()
        }
    })
    readonly communicationLabelsByProcurementId = new Map<string, string>()
    readonly _snapshotsByProcurementId = new Map<string, RHProcurementPrivateMessageThreadsSnapshot>()
    _roundContext?: ProcurementRoundContext

    _procurement?: RHProcurementPublic | RHProcurement
    _isCurrentUserActingAsProcurer = NO
    _canAnswerMessages = NO
    _selectedOffererCompanyId?: string
    _snapshot?: RHProcurementPrivateMessageThreadsSnapshot
    _loadGeneration = 0

    get isCurrentUserActingAsProcurer() { return this._isCurrentUserActingAsProcurer }
    set isCurrentUserActingAsProcurer(isCurrentUserActingAsProcurer: boolean) {
        this.configureAccessState({
            isCurrentUserActingAsProcurer,
            canAnswerMessages: this._canAnswerMessages
        })
    }

    get canAnswerMessages() { return this._canAnswerMessages }
    set canAnswerMessages(canAnswerMessages: boolean) {
        this.configureAccessState({
            isCurrentUserActingAsProcurer: this._isCurrentUserActingAsProcurer,
            canAnswerMessages
        })
    }


    configureAccessState(accessState: RHPrivateMessagingAccessState) {
        let shouldReloadVisibleView = YES
        if (accessState.shouldReloadVisibleView !== undefined) {
            shouldReloadVisibleView = accessState.shouldReloadVisibleView
        }
        const accessDidChange =
            this._isCurrentUserActingAsProcurer !== accessState.isCurrentUserActingAsProcurer ||
            this._canAnswerMessages !== accessState.canAnswerMessages
        this._isCurrentUserActingAsProcurer = accessState.isCurrentUserActingAsProcurer
        this._canAnswerMessages = accessState.canAnswerMessages
        this.threadListView.startConversationButton.hidden = !accessState.canAnswerMessages
        this.setNeedsLayout()
        if (
            accessDidChange &&
            shouldReloadVisibleView &&
            !this.hidden &&
            this._procurement
        ) {
            void this._reload()
        }
    }

    constructor(elementID?: string) {
        super(elementID)

        this.threadListView.threadDidSelect = (offererCompanyId: string) => {
            this._selectedOffererCompanyId = offererCompanyId
            this._renderSelectedThread()
        }
        this.threadListView.startConversationWasPressed = () => {
            if (!this._procurement) { return }
            void this.startDialog.showForProcurement(
                this.composeTargetDropdown.selectedValue ?? (this._procurement._id + ""),
                (startedOffererCompanyIds: string[]) => {
                    this._selectedOffererCompanyId = startedOffererCompanyIds[0]
                    void this._reload()
                }
            )
        }
        // No direct reload here: sendMessage() already notifies this view as a store subscriber.
    }


    override wasAddedToViewTree() {
        super.wasAddedToViewTree()
        RHCore.sharedInstance.privateMessageStore.subscribe(this, (
            procurementId: string,
            shouldReload: boolean
        ) => {
            if (this._procurement && (procurementId === (this._procurement._id + "") ||
                this.communicationLabelsByProcurementId.has(procurementId))) {
                if (shouldReload) {
                    void this._reload()
                }
                else {
                    this.invalidateForCompanyChange()
                }
            }
        })
    }

    override wasRemovedFromViewTree() {
        super.wasRemovedFromViewTree()
        RHCore.sharedInstance.privateMessageStore.unsubscribe(this)
    }


    async loadForProcurement(procurement: RHProcurementPublic | RHProcurement) {
        const procurementId = procurement._id + ""
        const isSwitchingProcurement = (this._procurement?._id + "") !== procurementId
        this._procurement = procurement
        if (isSwitchingProcurement) {
            this._invalidateLoadedState()
        }
        await this._reload()
    }


    selectOffererCompany(offererCompanyId: string) {
        this._selectedOffererCompanyId = offererCompanyId
    }

    configureCommunicationSources(labelsByProcurementId: Map<string, string>, roundContext?: ProcurementRoundContext) {
        this.communicationLabelsByProcurementId.clear()
        labelsByProcurementId.forEach((label, procurementId) => {
            this.communicationLabelsByProcurementId.set(procurementId, label)
        })
        this._roundContext = roundContext
    }


    invalidateForCompanyChange() {
        this._invalidateLoadedState()
    }


    /** Clears loaded thread data so a stale company's/procurement's content can't linger while the next context loads. */
    _invalidateLoadedState() {
        this._loadGeneration += 1
        this._snapshot = undefined
        this._snapshotsByProcurementId.clear()
        this._selectedOffererCompanyId = undefined
        this.setNeedsLayoutUpToRootView()
    }


    /** Never blanks `_snapshot` up front — that flashed the panel to "loading" on every same-procurement refresh (e.g. right after sending). */
    async _reload() {
        const loadGeneration = ++this._loadGeneration
        const procurement = this._procurement

        if (!procurement) {
            return
        }

        try {
            const procurementId = procurement._id + ""
            const sourceIds = [...this.communicationLabelsByProcurementId.keys()]
            if (!sourceIds.includes(procurementId)) {
                sourceIds.unshift(procurementId)
            }
            const sources = await Promise.all(sourceIds.map(async sourceId => {
                const sourceSnapshot = await RHCore.sharedInstance.privateMessageStore.retrieveThreadsForProcurement(sourceId)
                let threads = sourceSnapshot.threads
                const canAnswer = this._canAnswerMessagesForProcurement(sourceId)
                if (this._isCurrentUserActingAsProcurer && canAnswer) {
                    const companies = await RHCore.sharedInstance.privateMessageStore.retrieveEligibleOffererCompanies(sourceId)
                    threads = this._threadsWithEligibleCompaniesIncluded(threads, companies)
                }
                return { sourceId, snapshot: { ...sourceSnapshot, threads } }
            }))
            if (!this._isReloadResultCurrent(loadGeneration, procurementId)) {
                return
            }

            const threadsByCompanyId = new Map<string, RHProcurementPrivateMessageThread>()
            this._snapshotsByProcurementId.clear()
            for (const source of sources) {
                this._snapshotsByProcurementId.set(source.sourceId, source.snapshot)
                for (const thread of source.snapshot.threads) {
                    const merged = threadsByCompanyId.get(thread.offererCompanyId)
                    if (merged) {
                        merged.messages.push(...thread.messages)
                        merged.hasUnreadForCurrentViewer ||= thread.hasUnreadForCurrentViewer
                    }
                    else {
                        threadsByCompanyId.set(thread.offererCompanyId, { ...thread, messages: [...thread.messages] })
                    }
                }
            }
            const threads = [...threadsByCompanyId.values()]
            for (const thread of threads) {
                thread.messages.sort((first, second) => new Date(first.createdAt).getTime() - new Date(second.createdAt).getTime())
            }
            this._snapshot = { threads, notificationSnapshotAt: sources[0].snapshot.notificationSnapshotAt }
            this._applySnapshot()
        }
        catch (error) {
            if (this._isReloadResultCurrent(loadGeneration, procurement._id + "")) {
                console.error("[RHPrivateMessagingView] Failed to load private threads:", error)
            }
        }
    }


    _isReloadResultCurrent(loadGeneration: number, procurementId: string): boolean {
        if (loadGeneration !== this._loadGeneration || !this._procurement) {
            return NO
        }
        return this._procurement._id + "" === procurementId
    }

    _canAnswerMessagesForProcurement(procurementId: string): boolean {
        const information = this._roundContext?.informationByProcurementId.get(procurementId)
        if (information) {
            return information.capabilities?.canAnswerMessages === YES
        }
        return procurementId === this._procurement?._id?.toString() && this._canAnswerMessages
    }


    /**
     * Adds an empty, clickable row for every eligible company that has no
     * message yet, so the procurer can start typing directly instead of
     * always going through the bulk "Saada sõnum" dialog for a single
     * recipient. Threads with messages sort most-recent-first; companies
     * with no messages yet follow, alphabetically.
     */
    _threadsWithEligibleCompaniesIncluded(
        threads: RHProcurementPrivateMessageThread[],
        eligibleCompanies: RHOffererCompanyEligibleForPrivateMessage[]
    ): RHProcurementPrivateMessageThread[] {
        const threadsByCompanyId = new Map<string, RHProcurementPrivateMessageThread>(
            threads.map((thread): [string, RHProcurementPrivateMessageThread] => [thread.offererCompanyId, thread])
        )

        for (const company of eligibleCompanies) {
            if (!threadsByCompanyId.has(company.companyId)) {
                threadsByCompanyId.set(company.companyId, {
                    offererCompanyId: company.companyId,
                    offererCompanyName: company.companyName,
                    messages: [],
                    hasUnreadForCurrentViewer: NO
                })
            }
        }

        return Array.from(threadsByCompanyId.values()).sort((threadA, threadB) => {
            const lastMessageA = threadA.messages[threadA.messages.length - 1]
            const lastMessageB = threadB.messages[threadB.messages.length - 1]
            if (lastMessageA && lastMessageB) {
                return new Date(lastMessageB.createdAt).getTime() - new Date(lastMessageA.createdAt).getTime()
            }
            if (lastMessageA) { return -1 }
            if (lastMessageB) { return 1 }
            return threadA.offererCompanyName.localeCompare(threadB.offererCompanyName)
        })
    }


    _applySnapshot() {
        if (!this._snapshot) { return }

        if (this._isCurrentUserActingAsProcurer) {
            this.threadListView.configureWithThreads(this._snapshot.threads, this._selectedOffererCompanyId)
            this._selectedOffererCompanyId = this.threadListView.selectedOffererCompanyId
                ?? this._snapshot.threads[0]?.offererCompanyId
        }
        else {
            this._selectedOffererCompanyId = this._snapshot.threads[0]?.offererCompanyId
        }

        this._renderSelectedThread()
        this.setNeedsLayoutUpToRootView()
    }


    _renderSelectedThread() {
        if (!this._procurement || !this._snapshot) { return }

        const thread = this._snapshot.threads.find(
            candidate => candidate.offererCompanyId === this._selectedOffererCompanyId
        )
        const previousTargetId = this.composeTargetDropdown.selectedValue
        const targets = [...this._snapshotsByProcurementId.entries()].filter(([sourceId, snapshot]) =>
            snapshot.threads.some(candidate => candidate.offererCompanyId === thread?.offererCompanyId) &&
            (!this._isCurrentUserActingAsProcurer || this._canAnswerMessagesForProcurement(sourceId))
        ).map(([sourceId]) => ({
            value: sourceId,
            label: this.communicationLabelsByProcurementId.get(sourceId) ?? "Üldine"
        }))
        let targetId: string | undefined = targets[0]?.value
        if (targets.some(target => target.value === previousTargetId)) {
            targetId = previousTargetId
        }
        this.composeTargetDropdown.configureWithObject({ options: targets, selectedValue: targetId })
        if (this.communicationLabelsByProcurementId.size > 1 && targets.length > 0) {
            this.composeTargetDropdown.addedAsSubviewToView(this)
        }
        else {
            this.composeTargetDropdown.removeFromSuperview()
        }
        this.threadView.sourceLabelsByProcurementId = this.communicationLabelsByProcurementId

        if (this._isCurrentUserActingAsProcurer) {
            if (!thread) {
                this.threadView.removeFromSuperview()
                this.noSelectionLabel.addedAsSubviewToView(this)
                this.setNeedsLayoutUpToRootView()
                return
            }
            this.noSelectionLabel.removeFromSuperview()
            this.threadView.addedAsSubviewToView(this)
            this.threadView.configureWithThread({
                procurementId: targetId ?? (this._procurement._id + ""),
                offererCompanyId: thread.offererCompanyId,
                conversationTitle: thread.offererCompanyName,
                messages: thread.messages,
                currentViewerRole: "procurer",
                canSend: !!targetId
            })
        }
        else {
            this.threadView.addedAsSubviewToView(this)
            this.threadView.configureWithThread({
                procurementId: targetId ?? (this._procurement._id + ""),
                offererCompanyId: thread?.offererCompanyId ?? "",
                conversationTitle: "Vestlus hankijaga",
                messages: thread?.messages ?? [],
                currentViewerRole: "offerer",
                canSend: !!targetId
            })
        }

        for (const [sourceId, snapshot] of this._snapshotsByProcurementId) {
            const sourceThread = snapshot.threads.find(candidate => candidate.offererCompanyId === thread?.offererCompanyId)
            const loadGeneration = this._loadGeneration
            const activeCompanyId = RHCore.sharedInstance.companyAffiliationStore?.activeCompanyId
            if (sourceThread?.messages.length && RHCore.sharedInstance.notificationStore?.hasPendingNotificationReading) requestAnimationFrame(() => {
                if (!this.threadView.isMemberOfViewTree || this.hidden || loadGeneration !== this._loadGeneration ||
                    this._selectedOffererCompanyId !== sourceThread.offererCompanyId ||
                    activeCompanyId !== RHCore.sharedInstance.companyAffiliationStore.activeCompanyId) return
                RHCore.sharedInstance.notificationStore.markPendingNotificationAsReadIfContentMatches({
                    procurementId: sourceId, tab: "qa", eventTypes: ["privateMessage.sent"],
                    messageIds: sourceThread.messages.map(message => message._id)
                })
            })
            if (sourceThread?.hasUnreadForCurrentViewer) {
                RHCore.sharedInstance.privateMessageStore
                    .markThreadAsRead(sourceId, sourceThread.offererCompanyId)
                    .catch(error => console.error("[RHPrivateMessagingView] Failed to mark thread as read:", error))
            }
        }
    }


    override layoutSubviews() {
        super.layoutSubviews()

        const padding = this.core.paddingLength
        const bounds = this.contentBoundsWithInset(0)

        if (!this._snapshot) {
            this.threadListView.removeFromSuperview()
            this.threadView.removeFromSuperview()
            this.noSelectionLabel.removeFromSuperview()
            this.loadingLabel.addedAsSubviewToView(this)
            bounds.rectangleWithHeight(this.loadingLabel)
                .assignedAsFrameOfView(this.loadingLabel)
            return
        }
        this.loadingLabel.removeFromSuperview()

        if (this._isCurrentUserActingAsProcurer && this.threadListView.superview !== this) {
            this.threadListView.addedAsSubviewToView(this)
        }
        else if (!this._isCurrentUserActingAsProcurer) {
            this.threadListView.removeFromSuperview()
        }
        const conversation = this.threadView
        const showsPlaceholder = this.noSelectionLabel.superview === this
        bounds.rectangleWithHeight(0)
            .IF(this.composeTargetDropdown.superview === this)
            .rectangleWithHeight(this.composeTargetDropdown)
            .assignedAsFrameOfView(this.composeTargetDropdown)
            .rectangleForNextRow(padding, 0)
            .ENDIF()
            .IF(this._isCurrentUserActingAsProcurer && bounds.width >= 640)
            .TRANSFORM(origin => {
                const [listBounds, threadBounds] = origin.rectanglesBySplittingWidth(
                    [nil, 1], padding, [RHPrivateMessageThreadViewTokens.threadListWidth, nil]
                )
                listBounds.rectangleWithHeight(RHPrivateMessageThreadViewTokens.threadViewHeight)
                    .assignedAsFrameOfView(this.threadListView)
                return threadBounds
            })
            .ELSE_IF(this._isCurrentUserActingAsProcurer)
            .rectangleWithHeight(180)
            .assignedAsFrameOfView(this.threadListView)
            .rectangleForNextRow(padding, 0)
            .ENDIF()
            .IF(showsPlaceholder)
            .rectangleWithHeight(this.noSelectionLabel)
            .assignedAsFrameOfView(this.noSelectionLabel)
            .ELSE()
            .rectangleWithMaxWidth(RHPrivateMessageThreadViewTokens.offererThreadMaxWidth, 0.5)
            .rectangleWithHeight(conversation)
            .assignedAsFrameOfView(conversation)
            .ENDIF()
    }


    override didReceiveBroadcastEvent(event: UIViewBroadcastEvent) {
        super.didReceiveBroadcastEvent(event)

        if (event.name === UICore.broadcastEventName.WindowDidResize) {
            this.setNeedsLayoutUpToRootView()
        }
    }


}
